import { Button, Modal, Row } from "antd";
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  ExclamationCircleOutlined,
  DownOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import image from "../../../../../../images/bank-payment.svg";

type HowToPayModalProps = {
  isModalOpen: boolean;
  close: () => void;
  onPay: () => void;
};
export const HowToPayModal = ({
  isModalOpen,
  close,
  onPay,
}: HowToPayModalProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const toggleCollapse = useCallback(
    () => setCollapsed((prev: boolean) => !prev),
    [],
  );

  const title: ReactNode = useMemo(() => {
    return (
      <Row className="pt-6 px-8">
        <h3 className="h h3 w-500">How to Pay Online?</h3>
      </Row>
    );
  }, []);

  const footer: ReactNode = useMemo(() => {
    return (
      <Row className="justify-end px-6 py-6">
        <Button type="primary" size="large" shape="round" onClick={onPay}>
          Proceed to Payment
        </Button>
        <Button size="large" className="ml-2" shape="round" onClick={close}>
          Close
        </Button>
      </Row>
    );
  }, [close, onPay]);

  return (
    <Modal
      title={title}
      open={isModalOpen}
      onCancel={close}
      width={632}
      className="how-to-pay__modal"
      footer={footer}
    >
      <Row className="d-block pt-8 px-8 pb-3">
        <Row className="w-100 align-center warn__container pa-4 mb-6">
          <Row className="icon-container justify-center mr-4">
            <NotificationOutlined className="text rotate" />
          </Row>
          <Row className="d-block notification-desc">
            <p className="my-0 text w-500">New payment experience.</p>
            <p className="text-md my-0">
              We've redesigned the payment page with clearer steps and an
              improved layout.
            </p>
          </Row>
        </Row>
        <p className="my-0 text-md">
          Online payments can only be made using a checking or savings account,
          we do not accept credit cards or debit cards.
        </p>
        <Row className="my-6 align-center mb-2">
          <ExclamationCircleOutlined className="text important-label mr-1" />
          <p className="my-0 text-md w-500 important-label mr-1">IMPORTANT:</p>
          <p className="my-0 text-md">
            Before you hit the “Submit” button please be sure:
          </p>
        </Row>
        <Row className="d-block">
          <ol className="pl-4 text-md my-0">
            <li>
              Any additional instructions we need in order to process your
              payment correctly are included (e.g., how many payments are paid).
            </li>
            <li>
              All of the information is correct including the payment amount you
              are making.
            </li>
          </ol>
        </Row>
        <p className="mb-0 mt-2 text-md">
          Once the payment is submitted, the transaction cannot be reversed and
          you will receive an email receipt. However, the payment will not be
          deducted from your bank account for up to 3 business days.
        </p>
        <Row className="d-block my-6  pick-container">
          <img src={image} alt="bank-img" />
          <Row className="d-block mx-4" style={{ flex: 1 }}>
            <p className="mt-0 mb-4 text-sm title">
              A sample of a check in order to get your routing number for your
              bank and the account number with your bank.
            </p>
            <p className="my-0 text-sm title">
              If you are unsure of any of these numbers, please contact your
              bank before continuing.
            </p>
          </Row>
        </Row>
        <Row className="d-block">
          <Button
            type="link"
            icon={<DownOutlined />}
            className="pa-0"
            onClick={toggleCollapse}
          >
            ISSUES PROCESSING PAYMENTS?
          </Button>
          {!collapsed && (
            <Row className="d-block">
              <p className="my-2">
                If you're seeing an error while trying to complete your payment,
                it may be due to security protections built into our checkout
                system. Here's how to fix it:
              </p>
              <ul className="pl-5 mt-0 mb-6">
                <li>
                  Avoid public Wi-Fi: Try switching to a secure, private
                  network.
                </li>
                <li>
                  Disable VPNs or browser extensions: Some plugins may interfere
                  with checkout.
                </li>
                <li>
                  Use a private/incognito browser window: This can help bypass
                  cached data.
                </li>
                <li>
                  Clear your browser cache: Old data may trigger security
                  protection.
                </li>
                <li>If the issue persists, please contact our support team.</li>
              </ul>
            </Row>
          )}
        </Row>
        <p className="my-0 text-md">
          If you have any questions or concerns, please don't hesitate to
          contact our customer support team.
        </p>
      </Row>
    </Modal>
  );
};
